import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Clock, Phone, CheckCircle, XCircle, Voicemail, PhoneOff, Archive, Settings, Copy, Pencil, RefreshCw, FolderOpen, BarChart2, Play } from 'lucide-react';
import api from '../api';
import { useData } from '../DataContext';
import PromptAssistant from '../components/PromptAssistant';
import Modal from '../components/Modal';
import useModal from '../components/useModal';

// País → zona horaria (IANA) para que el agente sepa la fecha/hora local correcta.
// Default Colombia. Se guarda en campana.zona_horaria.
const PAISES = [
  { label: '🇨🇴 Colombia', tz: 'America/Bogota' },
  { label: '🇲🇽 México', tz: 'America/Mexico_City' },
  { label: '🇦🇷 Argentina', tz: 'America/Argentina/Buenos_Aires' },
  { label: '🇨🇱 Chile', tz: 'America/Santiago' },
  { label: '🇵🇪 Perú', tz: 'America/Lima' },
  { label: '🇪🇨 Ecuador', tz: 'America/Guayaquil' },
  { label: '🇵🇦 Panamá', tz: 'America/Panama' },
  { label: '🇻🇪 Venezuela', tz: 'America/Caracas' },
  { label: '🇧🇴 Bolivia', tz: 'America/La_Paz' },
  { label: '🇨🇷 Costa Rica', tz: 'America/Costa_Rica' },
  { label: '🇩🇴 Rep. Dominicana', tz: 'America/Santo_Domingo' },
  { label: '🇬🇹 Guatemala', tz: 'America/Guatemala' },
  { label: '🇺🇾 Uruguay', tz: 'America/Montevideo' },
  { label: '🇵🇾 Paraguay', tz: 'America/Asuncion' },
  { label: '🇺🇸 Estados Unidos (ET)', tz: 'America/New_York' },
  { label: '🇪🇸 España', tz: 'Europe/Madrid' },
];

const ESTADOS = [
  { value: 'Pendiente',   label: 'Pendiente',    color: 'var(--warning)', bg: 'rgba(250,204,21,0.1)',   icon: <Clock size={13}/> },
  { value: 'En proceso',  label: 'En proceso',   color: 'var(--info)', bg: 'rgba(96,165,250,0.1)',   icon: <Phone size={13}/> },
  { value: 'Finalizada',  label: 'Completada',   color: 'var(--success)', bg: 'var(--success-soft)',   icon: <CheckCircle size={13}/> },
  { value: 'Fallida',     label: 'Fallida',      color: 'var(--danger)', bg: 'var(--danger-soft)',  icon: <XCircle size={13}/> },
  { value: 'Rechazada',   label: 'Rechazada',    color: 'var(--danger)', bg: 'rgba(244,63,94,0.1)',    icon: '🚫' },
  { value: 'Buzon',       label: 'Buzón',        color: 'var(--accent)', bg: 'rgba(192,132,252,0.1)',  icon: <Voicemail size={13}/> },
  { value: 'No contesta', label: 'No contesta',  color: 'var(--warning)', bg: 'rgba(251,146,60,0.1)',   icon: <PhoneOff size={13}/> },
];

function getEstadoCfg(status) {
  return ESTADOS.find(e => e.value === status) || { label: status, color: 'var(--text-2)', bg: 'rgba(148,163,184,0.1)', icon: '•' };
}

function exportCSV(data, filename) {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const rows = data.map(r => headers.map(h => `"${(r[h] || '').toString().replace(/"/g, "'")}"`).join(','));
  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function ArchivedRow({ c }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ borderBottom: '1px solid var(--surface-3)' }}>
      <div onClick={() => setOpen(o => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 16, padding: '10px 14px',
        cursor: 'pointer', opacity: 0.5,
        transition: 'opacity 0.2s',
      }}
        onMouseEnter={e => e.currentTarget.style.opacity = 0.75}
        onMouseLeave={e => e.currentTarget.style.opacity = 0.5}
      >
        <span style={{ fontSize: 11, color: 'var(--text-3)', width: 14 }}>{open ? '▲' : '▼'}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-3)', flex: 1 }}>{c.nombre_campana}</span>
        <span style={{ fontSize: 10, color: 'var(--surface-2)' }}>{c.hora_inicio || '08:00'} — {c.hora_fin || '20:00'}</span>
        <span style={{ fontSize: 10, color: 'var(--surface-2)', marginLeft: 16 }}>
          {c.date_updated ? new Date(c.date_updated).toLocaleDateString('es-CO', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'}
        </span>
        <span style={{ fontSize: 10, color: 'var(--surface-2)', background: 'var(--bg)', padding: '2px 8px', borderRadius: 20, border: '1px solid var(--surface-3)' }}>📦 Archivada</span>
      </div>
      {open && (
        <div style={{ padding: '12px 44px 20px', background: 'var(--bg)' }}>
          <div className="r-apila" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 10, color: 'var(--surface-2)', marginBottom: 4 }}>HORARIO</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{c.hora_inicio || '08:00'} — {c.hora_fin || '20:00'}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--surface-2)', marginBottom: 4 }}>DÍAS</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                {(c.dias_semana || [1,2,3,4,5]).map(d => ['','L','M','X','J','V','S','D'][d]).join(', ')}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--surface-2)', marginBottom: 4 }}>TRANSFERENCIA</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{c.numero_transferencia || '—'}</div>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: 'var(--surface-2)', marginBottom: 6 }}>COMPORTAMIENTO IA</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', lineHeight: 1.7, whiteSpace: 'pre-wrap',
              background: 'var(--bg)', padding: 12, borderRadius: 8, border: '1px solid var(--surface-3)' }}>
              {c.comportamiento_ia || '—'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── TOOLS EDITOR COMPONENT ──────────────────────────────────────────
const SENTIMIENTOS = ['positivo', 'negativo', 'neutral'];
const TOOL_ESTADOS = ['Completada', 'No contesta', 'Rechazada', 'Reagendar', 'Fallida'];

function ToolsEditor({ tools = [], onChange }) {
  const [expandido, setExpandido] = React.useState(null);

  const agregar = () => {
    const nueva = { nombre: '', descripcion: '', resultado: '', estado: 'Completada', sentimiento: 'positivo' };
    onChange([...tools, nueva]);
    setExpandido(tools.length);
  };

  const actualizar = (idx, campo, valor) => {
    const nuevas = tools.map((t, i) => i === idx ? { ...t, [campo]: valor } : t);
    onChange(nuevas);
  };

  const eliminar = (idx) => {
    onChange(tools.filter((_, i) => i !== idx));
    setExpandido(null);
  };

  const cardStyle = {
    background: 'var(--bg)', border: '1px solid var(--surface-2)',
    borderRadius: 10, marginBottom: 10, overflow: 'hidden'
  };
  const headerStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 16px', cursor: 'pointer'
  };
  const inp = {
    background: 'var(--bg)', border: '1px solid var(--surface-2)', borderRadius: 8,
    color: 'var(--text)', padding: '8px 12px', fontSize: 13, width: '100%',
    boxSizing: 'border-box', marginBottom: 10
  };
  const sel = { ...inp, cursor: 'pointer' };
  const lbl = { fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 4 };

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Herramientas del agente</div>
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>Define qué acciones puede ejecutar el agente al finalizar la llamada</div>
        </div>
        <button type="button" onClick={agregar} style={{
          background: 'var(--accent-solid)', color: '#fff', border: 'none', borderRadius: 8,
          padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer'
        }}>+ Agregar</button>
      </div>

      {tools.length === 0 && (
        <div style={{ background: 'var(--bg)', border: '1px dashed var(--surface-2)', borderRadius: 10, padding: '20px', textAlign: 'center' }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>🔧</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Sin herramientas configuradas</div>
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>Agrega herramientas para que el agente registre resultados automáticamente</div>
        </div>
      )}

      {tools.map((tool, idx) => (
        <div key={idx} style={cardStyle}>
          <div style={headerStyle} onClick={() => setExpandido(expandido === idx ? null : idx)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 16 }}>
                {tool.sentimiento === 'positivo' ? <CheckCircle size={12} color='var(--success)'/> : tool.sentimiento === 'negativo' ? <XCircle size={12} color='var(--danger)'/> : <span style={{width:12,height:12,borderRadius:'50%',background:'var(--text-3)',display:'inline-block'}}/>}
              </span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                  {tool.nombre || 'Nueva herramienta'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                  {tool.resultado || 'Sin resultado definido'} · {tool.estado || 'Completada'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button type="button" onClick={e => { e.stopPropagation(); eliminar(idx); }}
                style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 16 }}>×</button>
              <span style={{ color: 'var(--text-3)', fontSize: 12 }}>{expandido === idx ? '▲' : '▼'}</span>
            </div>
          </div>

          {expandido === idx && (
            <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--surface)' }}>
              <div style={{ height: 12 }} />
              <label style={lbl}>Nombre de la herramienta</label>
              <input style={inp} placeholder="ej: registrar_asistencia"
                value={tool.nombre} onChange={e => actualizar(idx, 'nombre', e.target.value)} />
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: -8, marginBottom: 10 }}>
                Sin espacios ni caracteres especiales. El agente llamará esta herramienta por este nombre.
              </div>

              <label style={lbl}>¿Cuándo debe usarla?</label>
              <input style={inp} placeholder="ej: cuando el cliente confirme que asistirá a la cita"
                value={tool.descripcion} onChange={e => actualizar(idx, 'descripcion', e.target.value)} />

              <label style={lbl}>Resultado que se registra</label>
              <input style={inp} placeholder="ej: Confirmó asistencia"
                value={tool.resultado} onChange={e => actualizar(idx, 'resultado', e.target.value)} />

              <div className="r-apila" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={lbl}>Estado del contacto</label>
                  <select style={sel} value={tool.estado} onChange={e => actualizar(idx, 'estado', e.target.value)}>
                    {TOOL_ESTADOS.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Sentimiento</label>
                  <select style={sel} value={tool.sentimiento} onChange={e => actualizar(idx, 'sentimiento', e.target.value)}>
                    {SENTIMIENTOS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Campaigns() {
  const { campanas, setCampanas, refrescar } = useData();
  const { modal, confirm, alert: modalAlert } = useModal();
  const [showForm, setShowForm]             = useState(false);
  const [selected, setSelected]             = useState(null);
  const [contactos, setContactos]           = useState([]);
  const [stats, setStats]                   = useState(null);
  const [exportando, setExportando]         = useState(false);
  const [form, setForm]                     = useState({ nombre_campana: '', comportamiento_ia: '', hora_inicio: '08:00', hora_fin: '20:00', dias_semana: [1,2,3,4,5], fechas_bloqueadas: [], numero_transferencia: '', tools: [], zona_horaria: 'America/Bogota', muletillas: '', auto_reactivar: false });
  const [loading, setLoading]               = useState(false);
  const [launching, setLaunching]           = useState(false);
  const [uploading, setUploading]           = useState(false);
  const [uploadResult, setUploadResult]     = useState(null);
  const [deleting, setDeleting]             = useState(null);
  const [editando, setEditando]             = useState(null);
  const [editForm, setEditForm]             = useState({ nombre_campana: '', comportamiento_ia: '', hora_inicio: '08:00', hora_fin: '20:00', dias_semana: [1,2,3,4,5], fechas_bloqueadas: [], numero_transferencia: '', tools: [], amd_config: null, zona_horaria: 'America/Bogota', muletillas: '' });
  const [filtroEstado, setFiltroEstado]     = useState('todos');
  const [statusRelanzar, setStatusRelanzar] = useState([]);
  const [podPrep, setPodPrep] = useState(null); // {state, progress, message, eta} mientras se prepara el pod TTS
  const [barridosData, setBarridosData]       = useState([]);

  const [cancelando, setCancelando] = useState(false);

  const cancelarPendientes = async () => {
    if (!selected || pendientes === 0) return;
    const ok = await confirm({
      title: 'Cancelar pendientes',
      message: `Se cancelarán ${pendientes} ${pendientes === 1 ? 'contacto' : 'contactos'} que no alcanzaron a gestionarse. `
             + `No se volverán a marcar en los próximos barridos.\n\nEsta acción se puede revertir desde Directus si te equivocas.`,
      type: 'danger', icon: '🚫', confirmText: `Sí, cancelar ${pendientes}`, cancelText: 'No, dejarlos',
    });
    if (!ok) return;
    setCancelando(true);
    try {
      const r = await api.post('/contacts/cancelar-pendientes', { campana_id: selected.id });
      await modalAlert({
        title: 'Pendientes cancelados',
        message: `${r.data.cancelados} contactos quedaron marcados como Cancelado.`,
        type: 'success', icon: '✅', confirmText: 'Entendido',
      });
      cargarContactos(selected.id);
      refrescar();
    } catch (e) {
      await modalAlert({
        title: 'No se pudo cancelar',
        message: e?.response?.data?.detail || e.message,
        type: 'danger', icon: '⚠️', confirmText: 'Cerrar',
      });
    } finally {
      setCancelando(false);
    }
  };

  const cargarContactos = useCallback((campana_id) => {
    // Conteos REALES (sin tope): la tabla se pagina a 2000 filas, pero los totales
    // del header y los botones se toman de /contacts/stats (agregado en Directus),
    // así el cliente ve el total completo aunque tenga más de 2000 contactos.
    api.get(`/contacts/stats?campana_id=${campana_id}`)
      .then(r => setStats(r.data)).catch(() => {});
    api.get(`/contacts/?campana_id=${campana_id}&limit=2000`)
      .then(r => setContactos(r.data)).catch(() => {});
    api.get(`/campaigns/${campana_id}/barridos`)
      .then(r => setBarridosData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selected) return;
    const interval = setInterval(() => cargarContactos(selected.id), 5000);
    return () => clearInterval(interval);
  }, [selected, cargarContactos]);

  // Exporta TODOS los contactos de la campaña (no solo los 2000 de la tabla):
  // pagina el endpoint hasta traerlos todos y luego arma el CSV.
  const exportarContactos = async () => {
    if (!selected || exportando) return;
    setExportando(true);
    try {
      const PAGE = 500;
      let offset = 0, todos = [];
      while (offset < 100000) {
        const r = await api.get(`/contacts/?campana_id=${selected.id}&limit=${PAGE}&offset=${offset}`);
        const batch = r.data || [];
        todos = todos.concat(batch);
        if (batch.length < PAGE) break;
        offset += PAGE;
      }
      exportCSV(todos.map(c => {
        const din = c.datos_dinamicos || {};
        return {
          nombre: c.nombre, telefono: c.telefono, estado: c.status,
          barrido: c.numero_barrido, deuda: c.monto_deuda,
          resultado: c.resultado_llamada, sentimiento: c.sentimiento,
          costo_cop: c.costo_llamada, duracion: c.duracion_segundos,
          ...din
        };
      }), `contactos_${selected.nombre_campana}.csv`);
    } catch (e) {
      // silencioso: si falla, no descarga (el botón vuelve a habilitarse)
    } finally {
      setExportando(false);
    }
  };

  const clonar = (e, c) => {
    e.stopPropagation();
    setForm({
      nombre_campana:    `${c.nombre_campana} (copia)`,
      comportamiento_ia: c.comportamiento_ia || '',
      hora_inicio:       c.hora_inicio || '08:00',
      hora_fin:          c.hora_fin || '20:00',
      dias_semana:       c.dias_semana || [1,2,3,4,5],
      fechas_bloqueadas: c.fechas_bloqueadas || [],
      numero_transferencia: c.numero_transferencia || '',
      tools:             c.tools || [],
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const crear = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const r = await api.post('/campaigns/', form);
      setShowForm(false);
      setForm({ nombre_campana: '', comportamiento_ia: '', hora_inicio: '08:00', hora_fin: '20:00', dias_semana: [1,2,3,4,5], zona_horaria: 'America/Bogota', muletillas: '', auto_reactivar: false });
      setCampanas(prev => [r.data, ...prev]);
      refrescar();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error creando campaña');
    } finally { setLoading(false); }
  };

  const archivar = async (e, campana_id) => {
    e.stopPropagation();
    const ok = await confirm({ title: 'Archivar campaña', message: 'La campaña quedará archivada y no podrá modificarse. ¿Continuar?', type: 'danger', icon: '📦', confirmText: 'Sí, archivar', cancelText: 'Cancelar' });
    if (!ok) return;
    setDeleting(campana_id);
    if (selected?.id === campana_id) { setSelected(null); setContactos([]); setBarridosData([]); }
    try {
      await api.delete(`/campaigns/${campana_id}`);
      setCampanas(prev => prev.map(c => c.id === campana_id ? { ...c, archivado: true } : c));
    } catch {
      alert('Error archivando');
    } finally { setDeleting(null); }
  };

  const abrirEditar = (e, c) => {
    e.stopPropagation();
    setEditForm({
      nombre_campana: c.nombre_campana || '',
      comportamiento_ia: c.comportamiento_ia || '',
      hora_inicio: c.hora_inicio || '08:00',
      hora_fin: c.hora_fin || '20:00',
      amd_config: c.amd_config || null,
      dias_semana: c.dias_semana || [1,2,3,4,5],
      numero_transferencia: c.numero_transferencia || '',
      fechas_bloqueadas: c.fechas_bloqueadas || [],
      tools: c.tools || [],
      saludo_inicial: c.saludo_inicial || '',
      auto_reactivar: !!c.auto_reactivar,
      zona_horaria: c.zona_horaria || 'America/Bogota',
      muletillas: c.muletillas || '',
    });
    setEditando(c);
  };

  const guardarEdicion = async () => {
    try {
      await api.patch(`/campaigns/${editando.id}`, editForm);
      setCampanas(prev => prev.map(c => c.id === editando.id ? { ...c, ...editForm } : c));
      if (selected?.id === editando.id) setSelected(prev => ({ ...prev, ...editForm }));
      setEditando(null);
      refrescar();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error guardando cambios');
    }
  };

  const seleccionar = (c) => {
    if (selected?.id === c.id) { setSelected(null); setContactos([]); setBarridosData([]); }
    else { setSelected(c); setUploadResult(null); setFiltroEstado('todos'); setStatusRelanzar([]); cargarContactos(c.id); }
  };

  const subirCSV = async (e) => {
    const file = e.target.files[0];
    if (!file || !selected) return;
    setUploading(true); setUploadResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const r = await api.post(`/contacts/upload-csv?campana_id=${selected.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadResult({ ok: true, msg: `✅ ${r.data.inserted} contactos cargados` });
      cargarContactos(selected.id);
    } catch (err) {
      setUploadResult({ ok: false, msg: err.response?.data?.detail || 'Error subiendo CSV' });
    } finally { setUploading(false); e.target.value = ''; }
  };

  const lanzar = async (statusFiltro) => {
    if (!selected) return;
    const filtros   = statusFiltro || ['Pendiente'];
    const elegibles = contactos.filter(c => filtros.includes(c.status)).length;
    if (elegibles === 0) { alert(`No hay contactos con estado: ${filtros.join(', ')}`); return; }
    // Calcular estimado de duración
    let estimadoMsg = '';
    try {
      const [configR, llamadasR] = await Promise.all([
        api.get('/config/agent'),
        api.get(`/calls?limit=50&campana_id=${selected.id}`)
      ]);
      const maxConc = configR.data.max_concurrentes || 3;
      const llamadasHist = llamadasR.data || [];
      const duraciones = llamadasHist.map(l => parseInt(l.duracion_segundos) || 0).filter(d => d > 5);
      const sinHistorial = duraciones.length === 0;
      const durPromedio = sinHistorial ? 45 : Math.round(duraciones.reduce((a,b) => a+b, 0) / duraciones.length);
      const mins = Math.floor(Math.ceil(elegibles / maxConc) * durPromedio / 60);
      const horas = Math.floor(mins / 60);
      const tiempoStr = horas > 0 ? `~${horas}h ${mins % 60}min` : mins === 0 ? 'menos de 1 min' : `~${mins} min`;
      estimadoMsg = sinHistorial
        ? `

⏱️ Estimado: ${tiempoStr} · ${maxConc} simultáneas · sin historial, basado en 45s/llamada`
        : `

⏱️ Estimado: ${tiempoStr} · ${maxConc} simultáneas · ${durPromedio}s promedio (${duraciones.length} llamadas)`;
    } catch(e) {}
    const ok = await confirm({ title: 'Lanzar campaña', message: `¿Lanzar ${elegibles} ${elegibles === 1 ? 'contacto' : 'contactos'} con estado: ${filtros.join(', ')}?${estimadoMsg}`, type: 'info', icon: '🚀', confirmText: 'Lanzar', cancelText: 'Cancelar' });
    if (!ok) return;
    setLaunching(true);
    try {
      const r = await api.post('/dialer/launch', { campana_id: selected.id, limite: 40, status_filtro: filtros });
      if (r.data.preparando_pod) {
        // Preparando el servidor de voz (pod GPU): mostrar loading con progreso
        setPodPrep({ state: 'provisioning', progress: 5, message: 'Preparando tu campaña…', eta: null });
        const ready = await new Promise((resolve) => {
          const iv = setInterval(async () => {
            try {
              const s = (await api.get('/dialer/pod-status')).data;
              setPodPrep({ state: s.state, progress: s.progress || 0, message: s.message || 'Preparando tu campaña…', eta: s.eta_seconds });
              if (s.state === 'ready')  { clearInterval(iv); resolve(true); }
              // Nota: el gestor ya no emite 'error'; mantiene 'provisioning' y reintenta solo.
            } catch (e) {}
          }, 3000);
          setTimeout(() => { clearInterval(iv); resolve(false); }, 900000); // tope 15 min
        });
        setPodPrep(null);
        if (!ready) {
          await modalAlert({ title: 'Estamos preparando tu campaña', message: 'Hay bastante demanda en este momento. Tu campaña se sigue preparando y las llamadas comenzarán en cuanto esté lista. Puedes cerrar esto e intentar de nuevo en unos minutos.', type: 'info', icon: '⏳' });
          return;
        }
      }
      await modalAlert({ title: 'Campaña lanzada', message: `🚀 ${r.data.llamadas} llamadas ${r.data.preparando_pod ? 'iniciando' : 'disparadas exitosamente'}.`, type: 'success', icon: '🚀', confirmText: 'Perfecto' });
      cargarContactos(selected.id);
    } catch (err) {
      await modalAlert({ title: 'Error', message: err.response?.data?.detail || 'Error lanzando campaña', type: 'danger', icon: <XCircle size={13}/> });
    } finally { setLaunching(false); }
  };

  // Conteos REALES desde /contacts/stats (sin tope de 2000). Fallback al arreglo
  // cargado si stats aún no llegó.
  const porStatus = stats?.por_status || null;
  const cnt = (s) => porStatus ? (porStatus[s] || 0) : contactos.filter(c => c.status === s).length;
  const totalContactos = stats?.total ?? contactos.length;
  const totalProcesados = totalContactos - cnt('Pendiente') - cnt('En proceso');
  const resumenEstados = ESTADOS.map(e => ({
    ...e,
    count: cnt(e.value),
    pct: totalContactos > 0 ? Math.round((cnt(e.value) / totalContactos) * 100) : 0,
  }));

  // Detectar columnas dinámicas del CSV
  const columnasDinamicas = useMemo(() => {
    for (const c of contactos) {
      if (c.datos_dinamicos && typeof c.datos_dinamicos === 'object') {
        return Object.keys(c.datos_dinamicos);
      }
    }
    return [];
  }, [contactos]);

  const barridos = {};
  contactos.forEach(c => {
    const b = c.numero_barrido || 1;
    if (!barridos[b]) barridos[b] = { total: 0, estados: {} };
    barridos[b].total++;
    barridos[b].estados[c.status] = (barridos[b].estados[c.status] || 0) + 1;
  });

  const contactosFiltrados = filtroEstado === 'todos' ? contactos : contactos.filter(c => c.status === filtroEstado);
  const pendientes = cnt('Pendiente');

  return (
    <div>
      {podPrep && (
        <div style={{ position:'fixed', inset:0, background:'rgba(2,6,23,0.82)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div className="ic-card" style={{ background:'var(--bg)', border:'1px solid var(--accent-soft-2)', borderRadius:16, padding:'32px 40px', width:440, maxWidth:'90vw', textAlign:'center' }}>
            <div style={{ fontSize:16, fontWeight:700, color:'var(--text)', marginBottom:6 }}>Preparando tu campaña…</div>
            <div style={{ fontSize:12.5, color:'var(--text-2)', marginBottom:20, lineHeight:1.5 }}>Estamos dejando todo listo. Las llamadas comenzarán en cuanto tu campaña esté preparada.</div>
            <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', marginBottom:8, gap:12 }}>
              <span style={{ fontSize:12.5, color:'var(--accent)', fontWeight:600 }}>{podPrep.message}</span>
              <span className="num" style={{ fontSize:20, fontWeight:800, color:'var(--text)' }}>{podPrep.progress||0}%</span>
            </div>
            <div style={{ height:8, background:'var(--surface-2)', borderRadius:8, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${podPrep.progress||0}%`, background:'linear-gradient(90deg,var(--accent-solid),var(--accent))', transition:'width .5s' }} />
            </div>
            {podPrep.eta != null && podPrep.eta > 0 && (
              <div style={{ fontSize:11, color:'var(--text-3)', marginTop:10 }}>Tiempo estimado: ~{Math.ceil(podPrep.eta/60)} min</div>
            )}
          </div>
        </div>
      )}
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontFamily: 'Inter,sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Campañas</h1>
          <p style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 4 }}>{campanas.length} campañas creadas</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <PromptAssistant onApplyPrompt={p => setForm(prev => ({ ...prev, comportamiento_ia: p }))} />
          {localStorage.getItem('rol') !== 'consulta' && <button onClick={() => setShowForm(!showForm)} style={btnPrimaryStyle}>
            {showForm ? 'Cancelar' : '+ Nueva campaña'}
          </button>}
        </div>
      </div>

      {/* Formulario */}
      {showForm && (
        <div className="ic-card" style={{
          background: 'var(--bg)', border: '1px solid var(--accent-solid)', borderRadius: 16,
          padding: 28, marginBottom: 24, boxShadow: '0 8px 32px var(--accent-soft)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ color: 'var(--text)', fontSize: 16, fontWeight: 700 }}>Nueva campaña</h3>
            <PromptAssistant onApplyPrompt={p => setForm(prev => ({ ...prev, comportamiento_ia: p }))} />
          </div>
          <form onSubmit={crear}>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Nombre de la campaña</label>
              <input value={form.nombre_campana} onChange={e => setForm({ ...form, nombre_campana: e.target.value })}
                required style={inputStyle} placeholder="Ej: Cobros Junio 2026" />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Prompt del agente</label>
              <textarea value={form.comportamiento_ia} onChange={e => setForm({ ...form, comportamiento_ia: e.target.value })}
                required rows={8} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.7 }}
                placeholder="Usa el asistente IA para generar tu prompt, o escríbelo aquí..." />
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>
                Variables disponibles: <span style={{ color: 'var(--accent)' }}>{'{{nombre}}'}</span> <span style={{ color: 'var(--accent)' }}>{'{{monto_deuda}}'}</span> <span style={{ color: 'var(--accent)' }}>{'{{fecha_limite}}'}</span> + cualquier columna de tu CSV
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Saludo inicial <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(opcional)</span></label>
              <textarea value={form.saludo_inicial || ''} onChange={e => setForm({ ...form, saludo_inicial: e.target.value })}
                rows={2} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.7 }}
                placeholder="Hola {nombre}, le habla Esmeralda de SantaBPO. ¿Cómo está usted?" />
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>
                Se reproduce inmediatamente al contestar, sin esperar al LLM. Elimina el silencio inicial.
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>🌎 País / zona horaria</label>
              <select value={form.zona_horaria || 'America/Bogota'} onChange={e => setForm({ ...form, zona_horaria: e.target.value })} style={inputStyle}>
                {PAISES.map(p => <option key={p.tz} value={p.tz}>{p.label}</option>)}
              </select>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>
                El agente usa esta zona para saber la fecha/hora actual y acordar fechas de pago.
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>🗣️ Muletillas <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(opcional)</span></label>
              <textarea value={form.muletillas || ''} onChange={e => setForm({ ...form, muletillas: e.target.value })}
                rows={2} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.7 }}
                placeholder="Entiendo | Claro que sí | Permítame un momento | Con mucho gusto" />
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>
                Frases cortas separadas por “|”. Se pre-generan en la voz del agente y se reproducen tras cada respuesta del cliente para sonar más natural. Si lo dejas vacío, se usan las genéricas.
              </div>
            </div>
            {/* Horario */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Horario de llamadas</label>
              <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, color: 'var(--text-3)', display: 'block', marginBottom: 4 }}>Hora inicio</label>
                  <input type="time" value={form.hora_inicio} onChange={e => setForm({ ...form, hora_inicio: e.target.value })}
                    style={inputStyle} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, color: 'var(--text-3)', display: 'block', marginBottom: 4 }}>Hora fin</label>
                  <input type="time" value={form.hora_fin} onChange={e => setForm({ ...form, hora_fin: e.target.value })}
                    style={inputStyle} />
                </div>
              </div>
              <label style={{ fontSize: 11, color: 'var(--text-3)', display: 'block', marginBottom: 6 }}>Días activos</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[{d:1,l:'Lun'},{d:2,l:'Mar'},{d:3,l:'Mié'},{d:4,l:'Jue'},{d:5,l:'Vie'},{d:6,l:'Sáb'},{d:7,l:'Dom'}].map(({d,l}) => {
                  const activo = (form.dias_semana||[]).includes(d);
                  return (
                    <button key={d} type="button" onClick={() => {
                      const dias = form.dias_semana||[];
                      setForm({ ...form, dias_semana: activo ? dias.filter(x=>x!==d) : [...dias,d] });
                    }} style={{
                      padding: '5px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600,
                      border: activo ? '1px solid rgba(124,58,237,0.5)' : '1px solid var(--surface-2)',
                      background: activo ? 'var(--accent-soft-2)' : 'transparent',
                      color: activo ? 'var(--accent)' : 'var(--text-3)',
                    }}>{l}</button>
                  );
                })}
              </div>
            </div>
            {/* Relanzar pendientes automáticamente */}
            <div style={{ marginBottom: 16, padding: 14, background:'var(--surface-3)', borderRadius:10, border:'1px solid var(--surface-2)' }}>
              <label style={{ display:'flex', alignItems:'flex-start', gap:10, cursor:'pointer' }}>
                <input type="checkbox" checked={!!form.auto_reactivar}
                  onChange={e => setForm({ ...form, auto_reactivar: e.target.checked })}
                  style={{ marginTop:3, width:16, height:16, accentColor:'var(--accent-solid)', cursor:'pointer' }} />
                <span>
                  <span style={{ fontSize:13, fontWeight:600, color:'var(--accent)' }}>Relanzar pendientes automáticamente</span>
                  <span style={{ display:'block', fontSize:11, color:'var(--text-3)', marginTop:4, lineHeight:1.5 }}>
                    Si lo activas, el sistema retoma solo los contactos que queden pendientes (dentro del horario y días de arriba), incluso al día siguiente, hasta terminar la lista. Apagado = campaña de una sola corrida; la relanzas tú manualmente.
                  </span>
                </span>
              </label>
            </div>
            {/* Fechas bloqueadas */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>🚫 Fechas bloqueadas (además de festivos colombianos)</label>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 8 }}>
                Los festivos colombianos se bloquean automáticamente. Agrega fechas adicionales aquí.
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input type="date" id="nueva-fecha-bloqueo" style={{ ...inputStyle, flex: 1 }} />
                <button type="button" onClick={() => {
                  const val = document.getElementById('nueva-fecha-bloqueo').value;
                  if (!val) return;
                  const fechas = form.fechas_bloqueadas || [];
                  if (!fechas.includes(val)) setForm({ ...form, fechas_bloqueadas: [...fechas, val] });
                  document.getElementById('nueva-fecha-bloqueo').value = '';
                }} style={{ ...btnPrimaryStyle, padding: '8px 14px', fontSize: 12 }}>+ Agregar</button>
              </div>
              {(form.fechas_bloqueadas||[]).length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {(form.fechas_bloqueadas||[]).map(f => (
                    <span key={f} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '4px 10px', borderRadius: 6, fontSize: 11,
                      background: 'var(--danger-soft)', border: '1px solid rgba(248,113,113,0.25)',
                      color: 'var(--danger)',
                    }}>
                      {f}
                      <span style={{ cursor: 'pointer', opacity: 0.7 }} onClick={() =>
                        setForm({ ...form, fechas_bloqueadas: (form.fechas_bloqueadas||[]).filter(x => x !== f) })
                      }>×</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
            {/* Número de transferencia */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Número de transferencia a humano</label>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 8 }}>
                Si el cliente pide hablar con un asesor, la IA transferirá la llamada a este número. Déjalo vacío para desactivar.
              </div>
              <input value={form.numero_transferencia} onChange={e => setForm({ ...form, numero_transferencia: e.target.value })}
                placeholder="Ej: 3105994391" style={inputStyle} />
            </div>
            <ToolsEditor tools={form.tools} onChange={t => setForm(prev => ({ ...prev, tools: t }))} />
            <div style={{ display: 'flex', gap: 12 }}>
              <button type="submit" disabled={loading} style={btnPrimaryStyle}>
                {loading ? 'Creando...' : 'Crear campaña'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} style={btnSecStyle}>Cancelar</button>
            </div>
          </form>
        </div>
      )}

      {/* Grid campañas activas */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 16, marginBottom: 24 }}>
        {campanas.filter(c => !c.archivado).map(c => (
          <div key={c.id} onClick={() => seleccionar(c)} style={{
            background: 'var(--glass)', WebkitBackdropFilter: 'blur(20px) saturate(1.5)', backdropFilter: 'blur(20px) saturate(1.5)',
            borderRadius: 14, padding: 20, cursor: 'pointer',
            border: selected?.id === c.id ? '1px solid var(--accent-solid)' : '1px solid var(--surface)',
            position: 'relative', transition: 'all 0.25s ease',
            boxShadow: selected?.id === c.id ? '0 0 20px var(--accent-soft-2)' : 'none',
          }}>
            <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', gap: 6 }}>
              <button onClick={(e) => clonar(e, c)} title="Usar como plantilla (clonar sin contactos)" style={{
                background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.2)',
                color: 'var(--info)', borderRadius: 6, width: 26, height: 26,
                cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}><Copy size={13}/></button>
              <button onClick={(e) => abrirEditar(e, c)} style={{
                background: 'var(--accent-soft)', border: '1px solid var(--accent-soft-2)',
                color: 'var(--accent)', borderRadius: 6, width: 26, height: 26,
                cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}><Pencil size={13}/></button>
              <button onClick={(e) => archivar(e, c.id)} disabled={deleting === c.id} title="Archivar campaña" style={{
                background: 'var(--danger-soft)', border: '1px solid var(--danger-soft)',
                color: 'var(--danger)', borderRadius: 6, width: 26, height: 26,
                cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}><Archive size={13}/></button>
            </div>
            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', marginBottom: 8, paddingRight: 64 }}>{c.nombre_campana}</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.5 }}>
              {(c.comportamiento_ia || '').slice(0, 80)}...
            </div>
            <div style={{ marginTop: 14, fontSize: 11, color: 'var(--accent-solid)', fontWeight: 500 }}>
              {selected?.id === c.id ? '▲ Ver menos' : '▼ Gestionar'}
            </div>
          </div>
        ))}
        {campanas.filter(c => !c.archivado).length === 0 && (
          <div style={{ color: 'var(--text-3)', fontSize: 13, padding: 24 }}>No hay campañas — crea una arriba</div>
        )}
      </div>

      {/* Campañas archivadas */}
      {campanas.filter(c => c.archivado).length > 0 && (
        <div style={{ marginTop: 40, borderTop: '1px solid var(--surface-3)', paddingTop: 24 }}>
          <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Archive size={13} style={{display:'inline',marginRight:4}}/> Archivadas ({campanas.filter(c => c.archivado).length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {campanas.filter(c => c.archivado).map(c => (
              <ArchivedRow key={c.id} c={c} />
            ))}
          </div>
        </div>
      )}
      {/* Panel campaña */}
      {selected && (
        <div className="ic-card" style={{ background: 'var(--bg)', border: '1px solid var(--surface)', borderRadius: 16, padding: 28 }}>

          {/* Header campaña */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <div>
              <div style={{ fontFamily: 'Inter,sans-serif', fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{selected.nombre_campana}</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{totalContactos} contactos cargados</div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={exportarContactos} disabled={exportando} style={{ ...btnSecStyle, opacity: exportando ? 0.6 : 1, cursor: exportando ? 'wait' : 'pointer' }}>
                {exportando ? 'Exportando…' : '↓ Exportar'}
              </button>
              <button onClick={cancelarPendientes} disabled={cancelando || pendientes === 0} title="Marca los pendientes como Cancelado para que no se llamen despues" style={{
                padding:'10px 16px', borderRadius:10, fontSize:13, fontWeight:600, marginRight:8,
                border:'1px solid rgba(251,191,36,0.3)',
                background: pendientes === 0 ? 'var(--surface-2)' : 'rgba(251,191,36,0.10)',
                color: pendientes === 0 ? 'var(--text-3)' : 'var(--warning)',
                cursor: pendientes === 0 ? 'not-allowed' : 'pointer',
              }}>
                {cancelando ? 'Cancelando...' : `Cancelar pendientes${pendientes > 0 ? ` (${pendientes})` : ''}`}
              </button>
              <button onClick={() => lanzar(['Pendiente'])} disabled={launching || pendientes === 0} style={{
                ...btnPrimaryStyle,
                background: pendientes === 0 ? 'var(--surface-2)' : 'linear-gradient(135deg,var(--accent-solid),var(--accent-2))',
                color: pendientes === 0 ? 'var(--text-3)' : '#fff',
                cursor: pendientes === 0 ? 'not-allowed' : 'pointer',
                boxShadow: pendientes > 0 ? '0 4px 15px rgba(124,58,237,0.4)' : 'none',
              }}>
                {launching ? 'Lanzando...' : `Lanzar (${pendientes})`}
              </button>
            </div>
          </div>

          {/* Resumen estados */}
          <div className="r-kpi" style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 10, marginBottom: 24 }}>
            {resumenEstados.map(e => (
              <div key={e.value} onClick={() => setFiltroEstado(filtroEstado === e.value ? 'todos' : e.value)}
                style={{
                  background: filtroEstado === e.value ? e.bg : 'var(--surface-3)',
                  borderRadius: 10, padding: '10px 8px', cursor: 'pointer', textAlign: 'center',
                  border: `1px solid ${filtroEstado === e.value ? e.color : 'var(--surface-2)'}`,
                  transition: 'all 0.25s ease',
                  transform: filtroEstado === e.value ? 'translateY(-2px)' : 'none',
                  boxShadow: filtroEstado === e.value ? `0 4px 12px ${e.color}33` : 'none',
                }}>
                <div style={{ fontSize: 16, marginBottom: 4 }}>{e.icon}</div>
                <div style={{ fontSize: 10, color: 'var(--text-3)', marginBottom: 4 }}>{e.label}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: e.color }}>{e.count}</div>
                {e.count > 0 && (
                  <div style={{ fontSize: 10, color: e.color, opacity: 0.75, marginTop: 2 }}>{e.pct}%</div>
                )}
              </div>
            ))}
          </div>

          {/* Barridos desde tabla llamadas */}
          {barridosData.length > 0 && (
            <div style={{ marginBottom: 24, padding: 16, background: 'var(--surface-3)', borderRadius: 10, border: '1px solid var(--surface-2)' }}>
              <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
                Historial de barridos
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {barridosData.map((data) => {
                  // Un barrido = un lanzamiento. Mismas cifras que el correo de reporte.
                  const r = data.resultados || {};
                  const fila = (label, v, color) => (
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 11, color }}>
                      <span>{label}</span><span style={{ fontVariantNumeric: 'tabular-nums' }}>{(v || 0).toLocaleString('es-CO')}</span>
                    </div>
                  );
                  return (
                    <div key={data.numero} style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px', border: '1px solid var(--surface-2)', minWidth: 200, flex: '1 1 200px', maxWidth: 260 }}>
                      <div style={{ fontSize: 11, color: 'var(--accent-solid)', fontWeight: 700, marginBottom: 4 }}>Barrido {data.numero} · {data.fecha}</div>
                      {data.filtro?.length > 0 && <div style={{ fontSize: 10.5, color: 'var(--text-3)', marginBottom: 6 }}>{data.filtro.join(', ')}</div>}
                      <div style={{ fontSize: 12, color: 'var(--text)', marginBottom: 4 }}>
                        {(data.contactos_llamados || 0).toLocaleString('es-CO')} de {(data.programados || 0).toLocaleString('es-CO')} marcados
                      </div>
                      <div style={{ height: 5, borderRadius: 4, background: 'var(--surface-2)', overflow: 'hidden', marginBottom: 8 }}>
                        <div style={{ width: `${data.pct || 0}%`, height: '100%', background: 'var(--success)' }} />
                      </div>
                      <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--text-3)', marginBottom: 6 }}>
                        <span>Contacto <strong style={{ color: 'var(--text)' }}>{data.tasa_contacto || 0}%</strong></span>
                        <span>Acuerdos <strong style={{ color: 'var(--text)' }}>{r.acuerdo || 0}</strong></span>
                      </div>
                      {fila('Acuerdo de pago', r.acuerdo, 'var(--success)')}
                      {fila('Contestó sin acuerdo', r.contesto, 'var(--accent)')}
                      {fila('Contestó sin hablar', r.sin_hablar, 'var(--text-3)')}
                      {fila('Buzón de voz', r.buzon, 'var(--info)')}
                      {fila('No contestó', r.no_contesto, 'var(--text-3)')}
                      {fila('Rechazó', r.rechazo, 'var(--danger)')}
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-3)', marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--surface-2)' }}>
                        <span>Costo</span><span style={{ color: 'var(--text)', fontWeight: 600 }}>${Math.round(data.costo || 0).toLocaleString('es-CO')}</span>
                      </div>
                      {data.pendientes > 0 && <div style={{ fontSize: 10.5, color: 'var(--warning)', marginTop: 4 }}>{data.pendientes.toLocaleString('es-CO')} quedaron pendientes</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Relanzar */}
          <div style={{ marginBottom: 24, padding: 16, background: 'var(--surface-3)', borderRadius: 10, border: '1px dashed var(--surface-2)' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>Relanzar por estado</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              {ESTADOS.filter(e => !['Pendiente','En proceso'].includes(e.value)).map(e => {
                const count = contactos.filter(c => c.status === e.value).length;
                if (count === 0) return null;
                const activo = statusRelanzar.includes(e.value);
                return (
                  <button key={e.value} onClick={() => setStatusRelanzar(prev =>
                    activo ? prev.filter(s => s !== e.value) : [...prev, e.value]
                  )} style={{
                    padding: '6px 14px', borderRadius: 8,
                    border: `1px solid ${activo ? e.color : 'var(--surface-2)'}`,
                    background: activo ? e.bg : 'transparent', color: activo ? e.color : 'var(--text-3)',
                    fontSize: 12, cursor: 'pointer', transition: 'all 0.2s',
                    transform: activo ? 'translateY(-1px)' : 'none',
                  }}>
                    {e.icon} {e.label} ({count})
                  </button>
                );
              })}
            </div>
            {statusRelanzar.length > 0 && (
              <button onClick={() => lanzar(statusRelanzar)} disabled={launching} style={{
                ...btnPrimaryStyle, fontSize: 12, padding: '8px 18px',
              }}>
                {launching ? 'Lanzando...' : `Relanzar ${contactos.filter(c => statusRelanzar.includes(c.status)).length} contactos`}
              </button>
            )}
          </div>

          {/* CSV */}
          <div style={{ marginBottom: 24, padding: 20, background: 'var(--surface-3)', borderRadius: 10, border: '1px dashed var(--surface-2)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Subir contactos (CSV)</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 12 }}>
              Requeridas: <span style={{ color: 'var(--accent)' }}>nombre, telefono</span> · Cualquier columna extra se inyecta como variable en el prompt
            </div>
            <label style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '9px 18px', background: 'var(--surface-2)', border: '1px solid var(--text-3)',
              borderRadius: 8, fontSize: 12, color: 'var(--text-2)', cursor: 'pointer', fontWeight: 500,
              transition: 'all 0.2s',
            }}>
              {uploading ? 'Subiendo...' : 'Seleccionar CSV'}
              <input type="file" accept=".csv" onChange={subirCSV} style={{ display: 'none' }} disabled={uploading} />
            </label>
            {uploadResult && (
              <div style={{ marginTop: 10, fontSize: 13, color: uploadResult.ok ? 'var(--success)' : 'var(--danger)' }}>
                {uploadResult.msg}
              </div>
            )}
          </div>

          {/* Tabla */}
          {contactos.length > 0 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
                  {contactosFiltrados.length} de {contactos.length} contactos
                  {filtroEstado !== 'todos' && <span style={{ color: getEstadoCfg(filtroEstado).color }}> · {getEstadoCfg(filtroEstado).label}</span>}
                </span>
                {filtroEstado !== 'todos' && (
                  <button onClick={() => setFiltroEstado('todos')} style={{
                    fontSize: 11, color: 'var(--accent-solid)', background: 'transparent', border: 'none', cursor: 'pointer'
                  }}>× Limpiar filtro</button>
                )}
              </div>
              <div style={{ borderRadius: 10, overflowX: 'auto', border: '1px solid var(--surface-2)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: `180px 120px ${columnasDinamicas.map(() => '100px').join(' ')} 160px 60px 120px 80px`, padding: '10px 16px', background: 'var(--surface-3)', borderBottom: '1px solid var(--surface-2)', minWidth: 'max-content' }}>
                  {['Nombre','Teléfono', ...columnasDinamicas.map(c => c.replace(/_/g,' ')), 'Resultado','Barrido','Estado','Costo'].map(h => (
                    <span key={h} style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap', paddingRight: 8 }}>{h}</span>
                  ))}
                </div>
                {contactosFiltrados.slice(0, 100).map(c => {
                  const cfg = getEstadoCfg(c.status);
                  const din = c.datos_dinamicos || {};
                  return (
                    <div key={c.id} style={{
                      display: 'grid', gridTemplateColumns: `180px 120px ${columnasDinamicas.map(() => '100px').join(' ')} 160px 60px 120px 80px`,
                      padding: '10px 16px', borderBottom: '1px solid var(--surface-3)', alignItems: 'center',
                      minWidth: 'max-content', transition: 'background 0.15s',
                    }}>
                      <span style={{ fontSize: 13, color: 'var(--text)', paddingRight: 8 }}>{c.nombre}</span>
                      <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{c.telefono}</span>
                      {columnasDinamicas.map(col => (
                        <span key={col} style={{ fontSize: 11, color: 'var(--text-2)', paddingRight: 8 }}>{din[col] ?? '-'}</span>
                      ))}
                      <span style={{ fontSize: 11, color: 'var(--text-3)', paddingRight: 8 }} title={c.resultado_llamada || ''}>
                        {(c.resultado_llamada || '-').slice(0,25)}{(c.resultado_llamada||'').length > 25 ? '…' : ''}
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--accent-solid)', textAlign: 'center', fontWeight: 600 }}>{c.numero_barrido || 1}</span>
                      <span style={{ fontSize: 11, fontWeight: 500, padding: '3px 10px', borderRadius: 20, width: 'fit-content', color: cfg.color, background: cfg.bg }}>
                        {cfg.icon} {cfg.label}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--warning)' }}>
                        {c.costo_llamada ? `$${Math.round(parseFloat(c.costo_llamada)).toLocaleString('es-CO')}` : '-'}
                      </span>
                    </div>
                  );
                })}
                {contactosFiltrados.length > 100 && (
                  <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-3)', textAlign: 'center' }}>
                    Mostrando 100 de {contactosFiltrados.length}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {editando && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div className="ic-card" style={{
            background: 'var(--bg)', border: '1.5px solid var(--accent-solid)',
            borderRadius: 16, padding: 28, width: 560, maxWidth: '90vw',
            boxShadow: '0 20px 60px var(--accent-soft-2)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontFamily: 'Inter,sans-serif', fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Editar campaña</div>
              <button onClick={() => setEditando(null)} style={{ background: 'transparent', border: 'none', color: 'var(--text-3)', fontSize: 22, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ maxHeight: '75vh', overflowY: 'auto', paddingRight: 4 }}>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Nombre de la campaña</label>
                <input value={editForm.nombre_campana} onChange={e => setEditForm({ ...editForm, nombre_campana: e.target.value })} required style={inputStyle} />
              </div>
              <div className="r-apila" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>Hora inicio</label>
                  <input type="time" value={editForm.hora_inicio} onChange={e => setEditForm({ ...editForm, hora_inicio: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Hora fin</label>
                  <input type="time" value={editForm.hora_fin} onChange={e => setEditForm({ ...editForm, hora_fin: e.target.value })} style={inputStyle} />
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Días de operación</label>
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  {[['L',1],['M',2],['X',3],['J',4],['V',5],['S',6],['D',7]].map(([label, d]) => {
                    const dias = editForm.dias_semana || [];
                    const activo = dias.includes(d);
                    return <button key={d} type="button" onClick={() => setEditForm({ ...editForm, dias_semana: activo ? dias.filter(x=>x!==d) : [...dias,d] })} style={{ width:34, height:34, borderRadius:8, border: activo ? '1.5px solid var(--accent-solid)':'1px solid var(--surface-2)', background: activo?'var(--accent-soft)':'var(--surface-3)', color: activo?'var(--accent)':'var(--text-3)', fontSize:12, fontWeight:600, cursor:'pointer' }}>{label}</button>;
                  })}
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Número de transferencia</label>
                <input value={editForm.numero_transferencia} onChange={e => setEditForm({ ...editForm, numero_transferencia: e.target.value })} style={inputStyle} placeholder="ej: 3001234567" />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Prompt del agente</label>
                <PromptAssistant onApplyPrompt={p => setEditForm(prev => ({ ...prev, comportamiento_ia: p }))} />
                <textarea value={editForm.comportamiento_ia} onChange={e => setEditForm({ ...editForm, comportamiento_ia: e.target.value })} required rows={8} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.7, marginTop: 8 }} />
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>Variables: <span style={{ color: 'var(--accent)' }}>{'{{nombre}}'}</span> <span style={{ color: 'var(--accent)' }}>{'{{monto_deuda}}'}</span> + cualquier columna de tu CSV</div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Saludo inicial <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(opcional)</span></label>
                <textarea value={editForm.saludo_inicial || ''} onChange={e => setEditForm({ ...editForm, saludo_inicial: e.target.value })}
                  rows={2} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.7 }} 
                  placeholder="Hola {nombre}, le habla Esmeralda de SantaBPO. ¿Cómo está usted?" />
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>
                  Se reproduce inmediatamente al contestar, sin esperar al LLM. Elimina el silencio inicial.
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>🌎 País / zona horaria</label>
                <select value={editForm.zona_horaria || 'America/Bogota'} onChange={e => setEditForm({ ...editForm, zona_horaria: e.target.value })} style={inputStyle}>
                  {PAISES.map(p => <option key={p.tz} value={p.tz}>{p.label}</option>)}
                </select>
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>
                  El agente usa esta zona para la fecha/hora actual y acordar fechas de pago.
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>🗣️ Muletillas <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(opcional)</span></label>
                <textarea value={editForm.muletillas || ''} onChange={e => setEditForm({ ...editForm, muletillas: e.target.value })}
                  rows={2} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.7 }}
                  placeholder="Entiendo | Claro que sí | Permítame un momento | Con mucho gusto" />
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>
                  Frases cortas separadas por “|”. Se pre-generan en la voz del agente y se reproducen tras cada respuesta del cliente. Vacío = genéricas.
                </div>
              </div>
              <div style={{ marginBottom: 16, padding: 14, background:'var(--surface-3)', borderRadius:10, border:'1px solid var(--surface-2)' }}>
                <label style={{ display:'flex', alignItems:'flex-start', gap:10, cursor:'pointer' }}>
                  <input type="checkbox" checked={!!editForm.auto_reactivar}
                    onChange={e => setEditForm({ ...editForm, auto_reactivar: e.target.checked })}
                    style={{ marginTop:3, width:16, height:16, accentColor:'var(--accent-solid)', cursor:'pointer' }} />
                  <span>
                    <span style={{ fontSize:13, fontWeight:600, color:'var(--accent)' }}>Relanzar pendientes automáticamente</span>
                    <span style={{ display:'block', fontSize:11, color:'var(--text-3)', marginTop:4, lineHeight:1.5 }}>
                      El sistema revisa cada minuto y vuelve a marcar los contactos que quedaron pendientes,
                      siempre dentro del horario y los días configurados arriba. Si está apagado, debes relanzar la campaña manualmente.
                    </span>
                  </span>
                </label>
              </div>
              <ToolsEditor tools={editForm.tools} onChange={t => setEditForm(prev => ({ ...prev, tools: t }))} />
              {/* Configuracion AMD de deteccion de buzon */}
              <div style={{ marginBottom: 16, padding: 16, background: 'var(--surface-3)', borderRadius: 10, border: '1px solid var(--surface-2)' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', marginBottom: 12 }}>Detección de buzón de voz (AMD)</div>
                <div className="r-apila" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {[
                    { key: 'initial_silence', label: 'Silencio inicial (ms)', desc: 'Silencio máximo antes del saludo. Más alto = menos falsos buzones.', def: 2500 },
                    { key: 'greeting', label: 'Duración máx. saludo (ms)', desc: 'Si el saludo dura más de esto, se clasifica como buzón.', def: 1500 },
                    { key: 'after_greeting_silence', label: 'Silencio post-saludo (ms)', desc: 'Silencio tras el saludo que indica que es humano.', def: 800 },
                    { key: 'maximum_number_of_words', label: 'Palabras máximas', desc: 'Palabras máximas en saludo antes de clasificar como buzón.', def: 2 },
                    { key: 'total_analysis_time', label: 'Tiempo total análisis (ms)', desc: 'Tiempo máximo para decidir si es humano o máquina.', def: 5000 },
                  ].map(({ key, label, desc, def }) => {
                    const amd = editForm.amd_config || {};
                    return (
                      <div key={key}>
                        <label style={{ ...labelStyle, color: 'var(--text-2)' }}>{label}</label>
                        <input
                          type="number"
                          value={amd[key] ?? def}
                          onChange={e => setEditForm(prev => ({
                            ...prev,
                            amd_config: { ...(prev.amd_config || {}), [key]: parseInt(e.target.value) || def }
                          }))}
                          style={{ ...inputStyle, fontSize: 13 }}
                        />
                        <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 3 }}>{desc} (def: {def})</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 8 }}>
                <button type="button" onClick={() => setEditando(null)} style={btnSecStyle}>Cancelar</button>
                <button type="button" onClick={guardarEdicion} style={btnPrimaryStyle}>✓ Guardar cambios</button>
              </div>
            </div>
          </div>
        </div>
      )}
    <Modal {...modal} />
    </div>
  );
}

const btnPrimaryStyle = {
  background: 'linear-gradient(135deg,var(--accent-solid),var(--accent-2))', border: 'none', color: '#fff',
  padding: '10px 20px', borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  transition: 'all 0.25s ease', boxShadow: '0 4px 15px var(--accent-soft-2)',
};
const btnSecStyle = {
  background: 'transparent', border: '1px solid var(--surface-2)', color: 'var(--text-3)',
  padding: '10px 20px', borderRadius: 9, fontSize: 13, cursor: 'pointer',
  transition: 'all 0.2s ease',
};
const inputStyle = {
  width: '100%', padding: '10px 12px', background: 'var(--surface-3)', border: '1px solid var(--surface)',
  borderRadius: 9, color: 'var(--text)', fontSize: 13, outline: 'none',
  fontFamily: "'Plus Jakarta Sans',sans-serif", transition: 'border-color 0.2s',
};
const labelStyle = {
  fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase',
  letterSpacing: '0.08em', display: 'block', marginBottom: 8,
};
